import CRMDealsAPI from "dashboard/api/crm/deals";
import CRMPipelinesAPI from "dashboard/api/crm/pipelines";
import { throwErrorMessage } from "../utils/api";

const DEFAULT_FILTERS = {
  query: "",
  ownerIds: [],
  createdAtFrom: null,
  createdAtTo: null,
  amountMin: null,
  amountMax: null,
};

const PER_PAGE = 20;

export const state = {
  isPaginated: true,
  pipeline: null,
  pipelines: [],
  stagesById: {},
  stageOrder: [],
  dealsById: {},
  dealsByStageId: {},
  stageMeta: {},      // { [stageId]: { totalCount, totalPages, currentPage, perPage, amountTotals } }
  stageIsLoading: {}, // { [stageId]: bool }
  stageFetchToken: {}, // { [stageId]: number } — descarta respuestas de fetchStage que llegan fuera de orden
  filters: { ...DEFAULT_FILTERS },
  uiFlags: {
    isFetching: false,
    isUpdating: false,
    isLoadingPipelines: false,
    error: null,
  },
};

export const getters = {
  columnDeals: ($state) => (stageId) => {
    const ids = $state.dealsByStageId[stageId] || [];
    return ids.map((id) => $state.dealsById[id]);
  },

  columnTotals: ($state, $getters) => (stageId) => {
    const deals = $getters.filteredColumnDeals(stageId);
    // uniqueLeadCount: siempre desde deals visibles (aproximación cuando isPaginated)
    const leadIds = new Set();
    deals.forEach((deal) => {
      if (deal.primary_contact && deal.primary_contact.id) {
        leadIds.add(deal.primary_contact.id);
      }
    });
    if ($state.isPaginated) {
      const meta = $state.stageMeta[stageId] || {};
      return {
        sumByCurrency: { ...(meta.amountTotals || {}) },
        dealCount: meta.totalCount ?? deals.length,
        uniqueLeadCount: leadIds.size,
      };
    }
    const sumByCurrency = {};
    deals.forEach((deal) => {
      if (deal.amount_cents && deal.currency) {
        sumByCurrency[deal.currency] =
          (sumByCurrency[deal.currency] || 0) + deal.amount_cents;
      }
    });
    return {
      sumByCurrency,
      dealCount: deals.length,
      uniqueLeadCount: leadIds.size,
    };
  },

  columnWeightedTotals: ($state, $getters) => (stageId) => {
    const stage = $state.stagesById[stageId];
    const probability = stage ? stage.probability / 100 : 0;

    if ($state.isPaginated) {
      const amountTotals = $state.stageMeta[stageId]?.amountTotals || {};
      const weightedSumByCurrency = {};
      Object.entries(amountTotals).forEach(([currency, cents]) => {
        if (cents) {
          weightedSumByCurrency[currency] =
            (weightedSumByCurrency[currency] || 0) + cents * probability;
        }
      });
      return {
        weightedSumByCurrency,
        probability: stage ? stage.probability : 0,
      };
    }

    const deals = $getters.filteredColumnDeals(stageId);
    const weightedSumByCurrency = {};
    deals.forEach((deal) => {
      if (deal.amount_cents && deal.currency) {
        const weightedAmount = deal.amount_cents * probability;
        weightedSumByCurrency[deal.currency] =
          (weightedSumByCurrency[deal.currency] || 0) + weightedAmount;
      }
    });
    return {
      weightedSumByCurrency,
      probability: stage ? stage.probability : 0,
    };
  },

  pipelineTotals: ($state, $getters) => {
    const sumByCurrency = {};
    const weightedSumByCurrency = {};
    const leadIds = new Set();
    let dealCount = 0;

    $state.stageOrder.forEach((stageId) => {
      const stage = $state.stagesById[stageId];
      const probability = stage ? stage.probability / 100 : 0;
      // filteredColumnDeals devuelve deals en memoria (approx para uniqueLeadCount)
      const deals = $getters.filteredColumnDeals(stageId);

      if ($state.isPaginated) {
        const meta = $state.stageMeta[stageId] || {};
        dealCount += meta.totalCount || 0;
        Object.entries(meta.amountTotals || {}).forEach(([currency, cents]) => {
          if (cents) {
            sumByCurrency[currency] = (sumByCurrency[currency] || 0) + cents;
            weightedSumByCurrency[currency] =
              (weightedSumByCurrency[currency] || 0) + cents * probability;
          }
        });
      } else {
        dealCount += deals.length;
        deals.forEach((deal) => {
          if (deal.amount_cents && deal.currency) {
            sumByCurrency[deal.currency] =
              (sumByCurrency[deal.currency] || 0) + deal.amount_cents;
            const weightedAmount = deal.amount_cents * probability;
            weightedSumByCurrency[deal.currency] =
              (weightedSumByCurrency[deal.currency] || 0) + weightedAmount;
          }
        });
      }

      // uniqueLeadCount: siempre desde deals visibles (approximación cuando isPaginated)
      deals.forEach((deal) => {
        if (deal?.primary_contact?.id) leadIds.add(deal.primary_contact.id);
      });
    });

    return {
      sumByCurrency,
      weightedSumByCurrency,
      dealCount,
      uniqueLeadCount: leadIds.size,
    };
  },

  // Getter para determinar si un stage es de tipo "ganado"
  isWinningStage: ($state) => (stageId) => {
    const targetStage = $state.stagesById[stageId];
    return targetStage?.stage_type === "won";
  },

  // Deals de un stage aplicando filtros activos (client-side cuando !isPaginated)
  filteredColumnDeals: ($state) => (stageId) => {
    const ids = $state.dealsByStageId[stageId] || [];
    const deals = ids.map((id) => $state.dealsById[id]).filter(Boolean);

    // Cuando isPaginated=true el server ya filtró, devolver tal cual
    if ($state.isPaginated) return deals;

    const f = $state.filters;
    return deals.filter((deal) => {
      if (f.query) {
        const q = f.query.toLowerCase();
        const inTitle = deal.title?.toLowerCase().includes(q);
        const inPhone = deal.primary_contact?.phone_number?.toLowerCase().includes(q);
        const inContact = deal.primary_contact?.name?.toLowerCase().includes(q);
        if (!inTitle && !inPhone && !inContact) return false;
      }
      if (f.ownerIds.length > 0 && !f.ownerIds.includes(deal.owner_id))
        return false;
      // createdAtFrom/To son Unix timestamps en segundos (igual que deal.created_at del API)
      if (f.createdAtFrom && deal.created_at < f.createdAtFrom) return false;
      if (f.createdAtTo && deal.created_at > f.createdAtTo) return false;
      const amountUnits = (deal.amount_cents || 0) / 100;
      if (f.amountMin != null && amountUnits < f.amountMin) return false;
      if (f.amountMax != null && amountUnits > f.amountMax) return false;
      return true;
    });
  },

  // Owners únicos de los deals cargados en el pipeline actual
  pipelineOwners: ($state) => {
    const seen = new Set();
    const owners = [];
    Object.values($state.dealsById).forEach((deal) => {
      if (deal.owner_id && !seen.has(deal.owner_id)) {
        seen.add(deal.owner_id);
        const name =
          deal.owner?.available_name ||
          deal.owner?.name ||
          `Agente ${deal.owner_id}`;
        owners.push({ id: deal.owner_id, name });
      }
    });
    return owners.sort((a, b) => a.name.localeCompare(b.name));
  },

  hasActiveFilters: ($state) => {
    const f = $state.filters;
    return !!(
      f.query ||
      f.ownerIds.length ||
      f.createdAtFrom ||
      f.createdAtTo ||
      f.amountMin != null ||
      f.amountMax != null
    );
  },

  activeFilterCount: ($state) => {
    const f = $state.filters;
    let n = 0;
    if (f.query) n++;
    if (f.ownerIds.length) n++;
    if (f.createdAtFrom || f.createdAtTo) n++;
    if (f.amountMin != null || f.amountMax != null) n++;
    return n;
  },
};

export const actions = {
  async fetchPipelines({ commit }) {
    commit("SET_PIPELINES_LOADING", true);
    try {
      const response = await CRMPipelinesAPI.get();
      commit("SET_PIPELINES", response.data);
    } catch (error) {
      throwErrorMessage(error);
      commit("SET_ERROR", error);
    } finally {
      commit("SET_PIPELINES_LOADING", false);
    }
  },

  async createPipeline({ commit }, pipelineData) {
    try {
      const response = await CRMPipelinesAPI.create(pipelineData);
      return response;
    } catch (error) {
      commit("SET_ERROR", error);
      throw error;
    }
  },

  async updatePipeline({ commit }, { id, ...pipelineData }) {
    try {
      const response = await CRMPipelinesAPI.update(id, pipelineData);
      return response;
    } catch (error) {
      commit("SET_ERROR", error);
      throw error;
    }
  },

  async deletePipeline({ commit }, pipelineId) {
    try {
      await CRMPipelinesAPI.delete(pipelineId);
      return true;
    } catch (error) {
      throwErrorMessage(error);
      commit("SET_ERROR", error);
      throw error;
    }
  },

  async fetchBoard({ commit, dispatch, state }, { pipelineId }) {
    // Resetear filtros al cambiar de pipeline
    if (state.pipeline?.id !== pipelineId) {
      commit('RESET_FILTERS');
    }
    commit('SET_LOADING', true);
    try {
      // 1. Request liviano: solo pipeline + stages, sin deals (evita serializar todos en jbuilder)
      const response = await CRMDealsAPI.get({ pipelineId, structureOnly: true });
      const { pipeline, stages = [] } = response.data;
      // Inicializar estructura del board sin deals
      commit('SET_BOARD_DATA', { pipeline, stages, deals: [] });
      // 2. Cargar página 1 de cada stage en paralelo
      await Promise.all(
        stages.map((stage) =>
          dispatch('fetchStage', { pipelineId, stageId: stage.id, page: 1 })
        )
      );
    } catch (error) {
      commit('SET_ERROR', error);
      throwErrorMessage(error);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchStage({ commit, state }, { pipelineId, stageId, page = 1 }) {
    // Token de secuencia: si llega una respuesta más nueva para este stage antes de que
    // ésta resuelva, la respuesta vieja se descarta en vez de sobreescribir el estado.
    // Necesario porque applyFilters dispara un fetchStage en paralelo por cada stage del
    // pipeline, y al filtrar varias veces seguido las respuestas pueden llegar desordenadas.
    const token = (state.stageFetchToken[stageId] || 0) + 1;
    commit('SET_STAGE_FETCH_TOKEN', { stageId, token });
    commit('SET_STAGE_LOADING', { stageId, val: true });
    try {
      const f = state.filters;
      const response = await CRMDealsAPI.get({
        pipelineId,
        stageId,
        page,
        perPage: PER_PAGE,
        ownerIds:    f.ownerIds?.length > 0 ? f.ownerIds : undefined,
        titleSearch: f.query || undefined,
        amountMin:   f.amountMin != null ? f.amountMin : undefined,
        amountMax:   f.amountMax != null ? f.amountMax : undefined,
        // createdAtFrom/To son unix timestamps (s) → convertir a fecha ISO para el backend
        createdFrom: f.createdAtFrom ? new Date(f.createdAtFrom * 1000).toISOString().split('T')[0] : undefined,
        createdTo:   f.createdAtTo   ? new Date(f.createdAtTo   * 1000).toISOString().split('T')[0] : undefined,
      });
      if (state.stageFetchToken[stageId] !== token) return; // respuesta obsoleta, descartar
      const { deals = [], meta } = response.data;
      commit('SET_STAGE_PAGE_DEALS', { stageId, deals });
      if (meta) commit('SET_STAGE_META', { stageId, meta });
    } catch (error) {
      if (state.stageFetchToken[stageId] !== token) return;
      commit('SET_ERROR', error);
    } finally {
      if (state.stageFetchToken[stageId] === token) {
        commit('SET_STAGE_LOADING', { stageId, val: false });
      }
    }
  },

  async applyFilters({ commit, dispatch, state }, newFilters) {
    commit('SET_FILTERS', newFilters);
    if (!state.pipeline) return;
    // Con paginación: re-fetch página 1 de cada stage con los nuevos filtros
    await Promise.all(
      state.stageOrder.map((stageId) =>
        dispatch('fetchStage', { pipelineId: state.pipeline.id, stageId, page: 1 })
      )
    );
  },

  async updateDeal({ commit }, { dealId, dealData }) {
    try {
      const response = await CRMDealsAPI.update(dealId, dealData);
      commit('UPDATE_DEAL_LOCAL', response.data);
    } catch (error) {
      throw error;
    }
  },

  async deleteDeal({ commit, state }, { dealId }) {
    try {
      const deal = state.dealsById[dealId];
      await CRMDealsAPI.delete(dealId);
      commit('DELETE_DEAL_LOCAL', { dealId, stageId: deal?.stage_id });
    } catch (error) {
      commit("SET_ERROR", error);
    }
  },

  async reorderDeal({ commit }, { dealId, newPosition }) {
    try {
      await CRMDealsAPI.reorder({ id: dealId, position: newPosition });
      // REORDER_DEAL_LOCAL ya fue aplicado de forma optimista en StageColumn.handleReorderDeal.
      // No aplicarlo de nuevo para evitar un doble reorden (aunque sea idempotente).
    } catch (error) {
      commit("SET_ERROR", error);
    }
  },
};

export const mutations = {
  SET_PIPELINES($state, pipelines) {
    $state.pipelines = pipelines;
  },

  SET_PIPELINES_LOADING($state, status) {
    $state.uiFlags.isLoadingPipelines = status;
  },

  SET_BOARD_DATA($state, payload) {
    const { pipeline, stages = [], deals = [] } = payload;
    $state.pipeline = pipeline;
    $state.stagesById = {};
    $state.stageOrder = [];
    $state.dealsById = {};
    $state.dealsByStageId = {};

    stages
      .sort((a, b) => a.position - b.position)
      .forEach((stage) => {
        $state.stagesById[stage.id] = stage;
        $state.stageOrder.push(stage.id);
        $state.dealsByStageId[stage.id] = [];
      });

    deals.forEach((deal) => {
      $state.dealsById[deal.id] = deal;
      if (!$state.dealsByStageId[deal.stage_id]) {
        $state.dealsByStageId[deal.stage_id] = [];
      }
      $state.dealsByStageId[deal.stage_id].push(deal.id);
    });
  },

  UPDATE_DEAL_LOCAL($state, deal) {
    if (!deal || !deal.id) return;
    const existing = $state.dealsById[deal.id];
    // Si cambió de stage, mover entre las listas (solo si la stage vieja existe en el board)
    if (existing && existing.stage_id !== deal.stage_id) {
      const fromList = $state.dealsByStageId[existing.stage_id] || [];
      $state.dealsByStageId[existing.stage_id] = fromList.filter((id) => id !== deal.id);
      const toList = $state.dealsByStageId[deal.stage_id] || [];
      if (!toList.includes(deal.id)) {
        $state.dealsByStageId[deal.stage_id] = [...toList, deal.id];
      }
      // Ajustar totalCount y amountTotals. Este bloque cubre:
      // 1. Drag-and-drop normal: vuedraggable actualiza arrays vía SET_STAGE_DEALS pero NO
      //    cambia dealsById[dealId].stage_id, por lo que existing.stage_id es el original → entra.
      // 2. Move-to-won: MOVE_DEAL_LOCAL hizo el rollback visual (stage_id = fromStage),
      //    UPDATE_DEAL_LOCAL ve el cambio y ajusta cuando el servidor confirma el destino.
      // 3. Edición de deal con cambio de stage via formulario (sin drag previo).
      if ($state.isPaginated) {
        const newStageMeta = { ...$state.stageMeta };
        const fromMeta = $state.stageMeta[existing.stage_id];
        if (fromMeta) {
          const newFromTotals = { ...(fromMeta.amountTotals || {}) };
          if (existing.currency) {
            newFromTotals[existing.currency] = Math.max(
              0,
              (newFromTotals[existing.currency] || 0) - (existing.amount_cents || 0),
            );
          }
          newStageMeta[existing.stage_id] = {
            ...fromMeta,
            totalCount: Math.max(0, (fromMeta.totalCount || 0) - 1),
            amountTotals: newFromTotals,
          };
        }
        const toMeta = $state.stageMeta[deal.stage_id];
        if (toMeta) {
          const newToTotals = { ...(toMeta.amountTotals || {}) };
          if (deal.currency) {
            newToTotals[deal.currency] = (newToTotals[deal.currency] || 0) + (deal.amount_cents || 0);
          }
          newStageMeta[deal.stage_id] = {
            ...toMeta,
            totalCount: (toMeta.totalCount || 0) + 1,
            amountTotals: newToTotals,
          };
        }
        $state.stageMeta = newStageMeta;
      }
    } else if (existing && $state.isPaginated) {
      // Mismo stage: ajustar amountTotals si cambió el monto o la moneda (edición de deal)
      const amountChanged = existing.amount_cents !== deal.amount_cents;
      const currencyChanged = existing.currency !== deal.currency;
      if ((amountChanged || currencyChanged) && existing.stage_id) {
        const meta = $state.stageMeta[existing.stage_id];
        if (meta) {
          const newTotals = { ...(meta.amountTotals || {}) };
          if (existing.currency) {
            newTotals[existing.currency] = Math.max(
              0,
              (newTotals[existing.currency] || 0) - (existing.amount_cents || 0),
            );
          }
          if (deal.currency) {
            newTotals[deal.currency] = (newTotals[deal.currency] || 0) + (deal.amount_cents || 0);
          }
          $state.stageMeta = {
            ...$state.stageMeta,
            [existing.stage_id]: { ...meta, amountTotals: newTotals },
          };
        }
      }
    }
    $state.dealsById[deal.id] = { ...existing, ...deal };
  },

  DELETE_DEAL_LOCAL($state, { dealId, stageId }) {
    const deal = $state.dealsById[dealId]; // leer antes de borrar
    delete $state.dealsById[dealId];
    if (stageId) {
      $state.dealsByStageId[stageId] = ($state.dealsByStageId[stageId] || []).filter(
        (id) => id !== dealId,
      );
      // Ajustar totalCount y amountTotals en stageMeta al eliminar localmente.
      if ($state.isPaginated) {
        const meta = $state.stageMeta[stageId];
        if (meta) {
          const newTotals = { ...(meta.amountTotals || {}) };
          if (deal?.currency && deal?.amount_cents) {
            newTotals[deal.currency] = Math.max(
              0,
              (newTotals[deal.currency] || 0) - deal.amount_cents,
            );
          }
          $state.stageMeta = {
            ...$state.stageMeta,
            [stageId]: {
              ...meta,
              totalCount: Math.max(0, (meta.totalCount || 0) - 1),
              amountTotals: newTotals,
            },
          };
        }
      }
    }
  },

  MOVE_DEAL_LOCAL($state, { dealId, fromStageId, toStageId }) {
    const deal = $state.dealsById[dealId];
    const fromList = $state.dealsByStageId[fromStageId] || [];
    const toList = $state.dealsByStageId[toStageId] || [];
    $state.dealsByStageId[fromStageId] = fromList.filter((id) => id !== dealId);
    $state.dealsByStageId[toStageId] = [...toList, dealId];
    // Usar spread para inmutabilidad (evita mutación directa del objeto existente)
    if (deal) $state.dealsById[dealId] = { ...deal, stage_id: toStageId };
    // NOTA: totalCount y amountTotals NO se ajustan aquí.
    // Esta mutación se usa exclusivamente como rollback visual en el flujo move-to-won
    // (revierte el movimiento de vuedraggable ANTES de que el servidor confirme).
    // En ese punto los metadatos aún no fueron modificados por nadie → no hay nada que revertir.
    // Los ajustes de totalCount/amountTotals los hace UPDATE_DEAL_LOCAL al recibir la
    // respuesta del servidor (puede ver el cambio de stage_id porque vuedraggable usa
    // SET_STAGE_DEALS y NO actualiza dealsById[dealId].stage_id directamente).
  },

  MOVE_DEAL_ROLLBACK($state, { dealId, fromStageId }) {
    const currentStageId = $state.dealsById[dealId].stage_id;
    const currentList = $state.dealsByStageId[currentStageId] || [];
    $state.dealsByStageId[currentStageId] = currentList.filter(
      (id) => id !== dealId,
    );
    const fromList = $state.dealsByStageId[fromStageId] || [];
    $state.dealsByStageId[fromStageId] = [...fromList, dealId];
    $state.dealsById[dealId].stage_id = fromStageId;
  },

  REORDER_DEAL_LOCAL($state, { dealId, newPosition, stageId }) {
    const dealsList = $state.dealsByStageId[stageId] || [];
    const currentIndex = dealsList.findIndex((id) => id == dealId);

    if (currentIndex === -1) return;

    // Remover el deal de su posición actual
    const newDealsList = [...dealsList];
    newDealsList.splice(currentIndex, 1);

    // Insertar en la nueva posición (ajustada a índice 0-based)
    const insertIndex = Math.min(newPosition - 1, newDealsList.length);
    newDealsList.splice(insertIndex, 0, dealId);

    $state.dealsByStageId[stageId] = newDealsList;
  },

  SET_FILTERS($state, filters) {
    $state.filters = { ...$state.filters, ...filters };
  },

  RESET_FILTERS($state) {
    $state.filters = { ...DEFAULT_FILTERS };
  },

  SET_STAGE_DEALS($state, { stageId, dealIds }) {
    $state.dealsByStageId[stageId] = dealIds;
  },

  SET_STAGE_PAGE_DEALS($state, { stageId, deals }) {
    // Limpiar deals viejos de este stage de dealsById
    const oldIds = $state.dealsByStageId[stageId] || [];
    oldIds.forEach((id) => { delete $state.dealsById[id]; });
    // Insertar los nuevos
    const newIds = [];
    deals.forEach((deal) => {
      $state.dealsById[deal.id] = deal;
      newIds.push(deal.id);
    });
    $state.dealsByStageId[stageId] = newIds;
  },

  SET_STAGE_META($state, { stageId, meta }) {
    $state.stageMeta = {
      ...$state.stageMeta,
      [stageId]: {
        totalCount:   meta.total_count,
        totalPages:   meta.total_pages,
        currentPage:  meta.current_page,
        perPage:      meta.per_page,
        amountTotals: meta.amount_totals || {},
      },
    };
  },

  SET_STAGE_LOADING($state, { stageId, val }) {
    $state.stageIsLoading = { ...$state.stageIsLoading, [stageId]: val };
  },

  SET_STAGE_FETCH_TOKEN($state, { stageId, token }) {
    $state.stageFetchToken = { ...$state.stageFetchToken, [stageId]: token };
  },

  SET_LOADING($state, status) {
    $state.uiFlags.isFetching = status;
  },

  SET_ERROR($state, error) {
    $state.uiFlags.error = error;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
